/**
 * Unequal (Futoshiki) Validation
 *
 * Validates moves and checks puzzle completion.
 */

import {
  GameMode,
  GameState,
  CellFlags,
  DIRECTIONS,
  CheckResult,
} from './types';

/**
 * Clear all error flags from state
 */
export function clearErrors(state: GameState): void {
  const { order } = state;
  const errorMask = ~(
    CellFlags.ERROR |
    CellFlags.ERROR_UP |
    CellFlags.ERROR_RIGHT |
    CellFlags.ERROR_DOWN |
    CellFlags.ERROR_LEFT
  );

  for (let i = 0; i < order * order; i++) {
    state.flags[i] = (state.flags[i] ?? 0) & errorMask;
  }
}

/**
 * Check if a cell violates Latin square rules (duplicates in row/column)
 */
export function checkLatinSquareError(
  state: GameState,
  x: number,
  y: number,
  markErrors: boolean
): boolean {
  const { order, nums } = state;
  const value = nums[y * order + x] ?? 0;

  if (value === 0) return true; // Empty cells are valid

  let isValid = true;

  // Check column for duplicates
  for (let yy = 0; yy < order; yy++) {
    if (yy === y) continue;
    if ((nums[yy * order + x] ?? 0) === value) {
      isValid = false;
      break;
    }
  }

  // Check row for duplicates
  for (let xx = 0; xx < order; xx++) {
    if (xx === x) continue;
    if ((nums[y * order + xx] ?? 0) === value) {
      isValid = false;
      break;
    }
  }

  // Mark error flag if requested
  if (!isValid && markErrors) {
    state.flags[y * order + x] = (state.flags[y * order + x] ?? 0) | CellFlags.ERROR;
  }

  return isValid;
}

/**
 * Check if a cell satisfies all adjacency/inequality constraints
 */
export function checkConstraintError(
  state: GameState,
  x: number,
  y: number,
  markErrors: boolean
): boolean {
  const { order, mode, nums, flags } = state;
  const cellFlags = flags[y * order + x] ?? 0;
  const value = nums[y * order + x] ?? 0;

  if (value === 0) return true; // Empty cells are valid

  let isValid = true;

  for (const dir of DIRECTIONS) {
    const nx = x + dir.dx;
    const ny = y + dir.dy;

    // Skip out of bounds
    if (nx < 0 || nx >= order || ny < 0 || ny >= order) {
      continue;
    }

    const neighborValue = nums[ny * order + nx] ?? 0;
    if (neighborValue === 0) continue; // Can't validate against empty cell

    const hasConstraint = (cellFlags & dir.flag) !== 0;

    if (mode === GameMode.ADJACENT) {
      const difference = Math.abs(value - neighborValue);

      // If marked adjacent, must differ by exactly 1
      if (hasConstraint && difference !== 1) {
        isValid = false;
        if (markErrors) {
          state.flags[y * order + x] = (state.flags[y * order + x] ?? 0) | dir.errorFlag;
        }
      }

      // If NOT marked adjacent, must NOT differ by 1
      if (!hasConstraint && difference === 1) {
        isValid = false;
        if (markErrors) {
          state.flags[y * order + x] = (state.flags[y * order + x] ?? 0) | dir.errorFlag;
        }
      }
    } else {
      // MODE_UNEQUAL: This cell must be > neighbor if constraint exists
      if (hasConstraint && value <= neighborValue) {
        isValid = false;
        if (markErrors) {
          state.flags[y * order + x] = (state.flags[y * order + x] ?? 0) | dir.errorFlag;
        }
      }
    }
  }

  return isValid;
}

/**
 * Validate entire puzzle
 */
export function checkComplete(
  state: GameState,
  markErrors: boolean
): CheckResult {
  const { order, nums } = state;

  // Clear all error flags if marking errors
  if (markErrors) {
    clearErrors(state);
  }

  let result = CheckResult.COMPLETE;

  // Check every cell
  for (let y = 0; y < order; y++) {
    for (let x = 0; x < order; x++) {
      const value = nums[y * order + x] ?? 0;

      if (value === 0) {
        result = CheckResult.INCOMPLETE;
      } else {
        // Check Latin square uniqueness
        if (!checkLatinSquareError(state, x, y, markErrors)) {
          result = CheckResult.INVALID;
        }

        // Check adjacency/inequality constraints
        if (!checkConstraintError(state, x, y, markErrors)) {
          result = CheckResult.INVALID;
        }
      }
    }
  }

  return result;
}

/**
 * Check if a move is valid (before executing)
 */
export function isValidMove(
  state: GameState,
  x: number,
  y: number,
  value: number
): boolean {
  const { order, flags } = state;

  // Coordinate validation
  if (x < 0 || x >= order || y < 0 || y >= order) {
    return false;
  }

  // Value validation
  if (value < 0 || value > order) {
    return false;
  }

  // Immutability check
  const cellFlags = flags[y * order + x] ?? 0;
  if (cellFlags & CellFlags.IMMUTABLE) {
    return false;
  }

  return true;
}

/**
 * Check if a cell is immutable (given clue)
 */
export function isImmutable(state: GameState, x: number, y: number): boolean {
  const { order, flags } = state;
  const cellFlags = flags[y * order + x] ?? 0;
  return (cellFlags & CellFlags.IMMUTABLE) !== 0;
}

/**
 * Check if a cell has an error
 */
export function hasError(state: GameState, x: number, y: number): boolean {
  const { order, flags } = state;
  const cellFlags = flags[y * order + x] ?? 0;
  return (
    (cellFlags &
      (CellFlags.ERROR |
        CellFlags.ERROR_UP |
        CellFlags.ERROR_RIGHT |
        CellFlags.ERROR_DOWN |
        CellFlags.ERROR_LEFT)) !==
    0
  );
}

/**
 * Check if a constraint has an error
 */
export function hasConstraintError(
  state: GameState,
  x: number,
  y: number,
  direction: number
): boolean {
  const { flags } = state;
  const cellFlags = flags[y * state.order + x] ?? 0;
  const dir = DIRECTIONS[direction];
  if (!dir) return false;
  return (cellFlags & dir.errorFlag) !== 0;
}

/**
 * Check if a constraint is marked as spent
 */
export function isConstraintSpent(
  state: GameState,
  x: number,
  y: number,
  direction: number
): boolean {
  const { flags } = state;
  const cellFlags = flags[y * state.order + x] ?? 0;
  const dir = DIRECTIONS[direction];
  if (!dir) return false;
  return (cellFlags & dir.spentFlag) !== 0;
}
